/**
 * The resume details a recruiter can correct or add.
 *
 * The scan (resume_scans.profile, 0104) records what the CV says and is rewritten by every Rescan.
 * A recruiter's edit is kept apart (candidacies.resume_overrides, 0106) and wins when displayed, so
 * a Rescan never wipes a correction and the page can say which values a person typed in. Neither is
 * ever scored, ranked or gated on.
 */
import { z } from "zod";
export const RESUME_OVERRIDE_KEYS = [
    "totalYears",
    "lastCompany",
    "lastTitle",
    "lastWorkingYm",
    "currentlyWorking",
    "education",
    "phone",
    "email",
    "citiesWorked",
    "skills",
];
const text = (max) => z.string().trim().min(1).max(max);
const list = (max, each) => z.array(text(each)).max(max);
/** A PUT body: every key optional; `null` clears the recruiter's value (back to what the scan read). */
export const resumeOverridesSchema = z
    .object({
    totalYears: z.number().min(0).max(60).nullable().optional(),
    lastCompany: text(120).nullable().optional(),
    lastTitle: text(120).nullable().optional(),
    lastWorkingYm: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use YYYY-MM").nullable().optional(),
    currentlyWorking: z.boolean().nullable().optional(),
    education: z
        .array(z.object({ degree: text(120), institution: text(160).optional(), year: text(10).optional() }).strict())
        .max(8)
        .nullable()
        .optional(),
    phone: text(30).nullable().optional(),
    email: z.string().trim().email().max(200).nullable().optional(),
    citiesWorked: list(12, 60).nullable().optional(),
    skills: list(40, 60).nullable().optional(),
})
    .strict();
/** Apply a PUT onto the stored overrides: a value sets the key, null removes it. Unknown keys never get in. */
export function applyOverrideEdit(stored, edit) {
    const out = {};
    for (const k of RESUME_OVERRIDE_KEYS)
        if (stored && stored[k] != null)
            out[k] = stored[k];
    for (const k of RESUME_OVERRIDE_KEYS) {
        const v = edit[k];
        if (v === undefined)
            continue;
        if (v === null)
            delete out[k];
        else
            out[k] = v;
    }
    return out;
}
/**
 * What the page shows: the scan's profile with the recruiter's values laid over it, and the list of keys a
 * person set. Setting a last working month means "not currently working" and vice versa, so the two never
 * contradict each other.
 */
export function mergeResumeProfile(scan, overrides) {
    const profile = { ...(scan ?? {}) };
    const overridden = [];
    for (const k of RESUME_OVERRIDE_KEYS) {
        const v = overrides?.[k];
        if (v == null)
            continue;
        profile[k] = v;
        overridden.push(k);
    }
    if (overrides?.lastWorkingYm != null && overrides.currentlyWorking == null)
        delete profile.currentlyWorking;
    if (overrides?.currentlyWorking === true)
        delete profile.lastWorkingYm;
    return { profile: profile, overridden };
}
//# sourceMappingURL=resumeProfile.js.map